import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  LIST_ADMIN_USERS,
  CREATE_ADMIN_USER,
  UPDATE_ADMIN_USER,
  DEACTIVATE_ADMIN_USER,
  GET_CURRENT_ADMIN,
} from '../graphql/admin';
import { AdminLayout } from '../components/AdminLayout';

export function AdminTeamPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<any>(null);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    role: 'support',
    allowedIpAddresses: '',
    notes: '',
  });

  const { data: currentAdminData } = useQuery(GET_CURRENT_ADMIN);
  const { data, loading, refetch } = useQuery(LIST_ADMIN_USERS);

  const [createAdmin, { loading: creating }] = useMutation(CREATE_ADMIN_USER, {
    onCompleted: () => {
      setShowCreateForm(false);
      resetForm();
      refetch();
    },
  });

  const [updateAdmin, { loading: updating }] = useMutation(UPDATE_ADMIN_USER, {
    onCompleted: () => {
      setEditingAdmin(null);
      resetForm();
      refetch();
    },
  });

  const [deactivateAdmin] = useMutation(DEACTIVATE_ADMIN_USER, {
    onCompleted: () => refetch(),
  });

  const currentAdmin = currentAdminData?.currentAdminUser;
  const admins = data?.adminListAdmins || [];

  // Check if current user is super admin
  const isSuperAdmin = currentAdmin?.role === 'super_admin';

  const resetForm = () => {
    setFormData({
      email: '',
      fullName: '',
      role: 'support',
      allowedIpAddresses: '',
      notes: '',
    });
  };

  const handleEdit = (admin: any) => {
    setEditingAdmin(admin);
    setFormData({
      email: admin.email,
      fullName: admin.fullName,
      role: admin.role,
      allowedIpAddresses: admin.allowedIpAddresses?.join(', ') || '',
      notes: admin.notes || '',
    });
    setShowCreateForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const input: any = {
      email: formData.email.trim().toLowerCase(),
      fullName: formData.fullName.trim(),
      role: formData.role,
    };

    if (formData.allowedIpAddresses) {
      input.allowedIpAddresses = formData.allowedIpAddresses
        .split(',')
        .map((ip) => ip.trim())
        .filter((ip) => ip);
    }

    if (formData.notes) {
      input.notes = formData.notes.trim();
    }

    if (editingAdmin) {
      await updateAdmin({
        variables: {
          adminUserId: editingAdmin.id,
          updates: input,
        },
      });
    } else {
      await createAdmin({ variables: { input } });
    }
  };

  const handleDeactivate = async (adminUserId: number) => {
    if (window.confirm('Are you sure you want to deactivate this admin user?')) {
      await deactivateAdmin({ variables: { adminUserId } });
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-800';
      case 'support':
        return 'bg-blue-100 text-blue-800';
      case 'billing':
        return 'bg-green-100 text-green-800';
      case 'marketing':
        return 'bg-purple-100 text-purple-800';
      case 'developer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRolePermissions = (role: string) => {
    const permissions: Record<string, string[]> = {
      super_admin: ['All permissions', 'Manage admins', 'Full system access'],
      support: ['View users', 'Manage users', 'View organizations', 'Manage organizations'],
      billing: ['View billing', 'Manage billing', 'View analytics'],
      marketing: ['View promo codes', 'Manage promo codes', 'View analytics'],
      developer: ['View system', 'View analytics', 'View audit logs'],
    };
    return permissions[role] || [];
  };

  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
            <p className="text-red-700">
              Only Super Admins can manage the admin team. Your current role is:{' '}
              <strong>{currentAdmin?.role}</strong>
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Team Management</h1>
            <p className="text-gray-600 mt-1">
              Manage admin users and their permissions ({admins.length} total)
            </p>
          </div>
          <button
            onClick={() => {
              setEditingAdmin(null);
              resetForm();
              setShowCreateForm(!showCreateForm);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {showCreateForm ? 'Cancel' : '+ Add Admin User'}
          </button>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingAdmin ? 'Edit Admin User' : 'Create New Admin User'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={!!editingAdmin}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Must match their Clerk authentication email
                  </p>
                </div>

                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="support">Support</option>
                    <option value="billing">Billing</option>
                    <option value="marketing">Marketing</option>
                    <option value="developer">Developer</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                  <div className="mt-2 text-xs text-gray-600">
                    <strong>Permissions:</strong>
                    <ul className="list-disc list-inside mt-1">
                      {getRolePermissions(formData.role).map((perm, i) => (
                        <li key={i}>{perm}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Allowed IP Addresses */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed IP Addresses (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.allowedIpAddresses}
                    onChange={(e) =>
                      setFormData({ ...formData, allowedIpAddresses: e.target.value })
                    }
                    placeholder="192.168.1.1, 10.0.0.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Comma-separated IPs. Leave empty to allow from any IP.
                  </p>
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Internal notes about this admin user..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <button
                  type="submit"
                  disabled={creating || updating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {creating || updating
                    ? editingAdmin
                      ? 'Updating...'
                      : 'Creating...'
                    : editingAdmin
                    ? 'Update Admin'
                    : 'Create Admin'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingAdmin(null);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Admin Users List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-pulse">Loading admin users...</div>
            </div>
          ) : admins.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No admin users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Admin User
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Role
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      IP Restrictions
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Last Login
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Created
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin: any) => (
                    <tr
                      key={admin.id}
                      className={`border-b hover:bg-gray-50 ${
                        admin.id === currentAdmin?.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-gray-900">
                            {admin.fullName}
                            {admin.id === currentAdmin?.id && (
                              <span className="ml-2 text-xs text-blue-600">(You)</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">{admin.email}</div>
                          {admin.isMfaEnabled && (
                            <div className="text-xs text-green-600 mt-1">MFA Enabled</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getRoleBadgeColor(admin.role)}`}>
                          {admin.role.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {admin.allowedIpAddresses && admin.allowedIpAddresses.length > 0 ? (
                          <div className="text-gray-900">
                            {admin.allowedIpAddresses.length} IP(s)
                          </div>
                        ) : (
                          <div className="text-gray-500">Any IP</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {admin.lastLoginAt ? (
                          <div>
                            <div>{formatDate(admin.lastLoginAt)}</div>
                            {admin.lastLoginIp && (
                              <div className="text-xs text-gray-500 font-mono">
                                {admin.lastLoginIp}
                              </div>
                            )}
                          </div>
                        ) : (
                          'Never'
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(admin.createdAt)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            admin.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {admin.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(admin)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Edit
                          </button>
                          {admin.isActive && admin.id !== currentAdmin?.id && (
                            <button
                              onClick={() => handleDeactivate(admin.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Deactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Security Note */}
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h3 className="font-semibold text-yellow-900 mb-2">Security Best Practices</h3>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Admin users must authenticate via Clerk using the email specified above</li>
            <li>• Enable MFA for all admin accounts in Clerk dashboard</li>
            <li>• Use IP whitelisting for highly sensitive roles (super_admin, billing)</li>
            <li>
              • All admin actions are logged in the audit log and cannot be deleted
            </li>
            <li>• Review admin access regularly and remove unused accounts</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
}
